import { Check, Copy, Film, Gift, ImageIcon, Pencil, Plus, RotateCcw, Save, Search, Trash2, UploadCloud, UserRoundPlus, X } from "lucide-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { adminRequest, adminUploadRequest } from "./api";
import { useAdminAuth } from "./AuthProvider";
import { SHAREABLE_VIDEO_MAX_BYTES, uploadShareableVideo } from "./shareableVideoUpload";
import type { AdminCatalogProduct, AdminOrderRefund, AdminOrderRefundContext, Seller, SellerBonus, SellerBonusAward, SellerConfig, SellerInvitation, ShareableContent, ShareChannel } from "./types";

const defaultConfig: SellerConfig = {
  activationMode: "manual",
  defaultActivationFlow: "starter",
  defaultSellerMode: "starter",
  progressionMode: "automatic",
  onlineSellerOrdersEnabled: false,
  legacySellerStoresEnabled: false,
  sellerDiscountStacking: "best_benefit",
  leaderOverrideRate: 0.03,
  levelRules: [
    {
      id: "starter_20",
      level: "starter_20",
      label: "Starter 20%",
      sellerMode: "starter",
      mode: "starter",
      rank: 10,
      commissionRate: 0.2,
      autoAssignable: true,
      defaultForMode: true,
      active: true,
      conditions: { minDisbursedOrders: 0, minDisbursedSalesAmount: 0, currency: "COP" }
    }
  ],
  payoutMinimums: { COP: 100000 }
};

type SellerMode = "starter" | "pro" | "leader";

function percent(value?: number | null) {
  return `${Math.round((value || 0) * 100)}%`;
}

function adminMoney(amount: number, currency: string) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(amount);
}

export function SellerConfigPanel() {
  const auth = useAdminAuth();
  const [config, setConfig] = useState<SellerConfig>(defaultConfig);

  const load = useCallback(async () => {
    if (!auth.token) return;
    setConfig(await adminRequest<SellerConfig>(auth.token, "/v1/admin/seller-config"));
  }, [auth.token]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateLevelRule(index: number, patch: Partial<SellerConfig["levelRules"][number]>) {
    setConfig((current) => ({
      ...current,
      levelRules: current.levelRules.map((rule, ruleIndex) => {
        if (ruleIndex !== index) return rule;
        const updated = { ...rule, ...patch };
        return {
          ...updated,
          level: updated.id,
          mode: updated.sellerMode
        };
      })
    }));
  }

  function updateLevelRuleConditions(
    index: number,
    patch: Partial<SellerConfig["levelRules"][number]["conditions"]>
  ) {
    setConfig((current) => ({
      ...current,
      levelRules: current.levelRules.map((rule, ruleIndex) => (
        ruleIndex === index
          ? { ...rule, conditions: { ...rule.conditions, ...patch } }
          : rule
      ))
    }));
  }

  function addLevelRule() {
    setConfig((current) => {
      const rank = Math.max(0, ...current.levelRules.map((rule) => rule.rank || 0)) + 10;
      const id = `pro_${rank}`;
      return {
        ...current,
        levelRules: [
          ...current.levelRules,
          {
            id,
            level: id,
            label: `Pro ${rank}`,
            sellerMode: "pro",
            mode: "pro",
            rank,
            commissionRate: 0.25,
            autoAssignable: true,
            defaultForMode: false,
            active: true,
            conditions: { minDisbursedOrders: 0, minDisbursedSalesAmount: 0, currency: "COP" }
          }
        ]
      };
    });
  }

  function removeLevelRule(index: number) {
    setConfig((current) => ({
      ...current,
      levelRules: current.levelRules.filter((_, ruleIndex) => ruleIndex !== index)
    }));
  }

  async function save() {
    if (!auth.token) return;
    setConfig(await adminRequest<SellerConfig>(auth.token, "/v1/admin/seller-config", {
      method: "PUT",
      body: JSON.stringify(config)
    }));
  }

  return (
    <section className="promotion-editor">
      <div className="promotion-editor-heading">
        <div>
          <span>Seller baseline</span>
          <h2>Configuracion</h2>
        </div>
        <button className="primary-button" type="button" onClick={() => void save()}><Save size={16} /> Guardar</button>
      </div>
      <div className="form-grid">
        <label>Activacion
          <select value={config.activationMode} onChange={(event) => setConfig({ ...config, activationMode: event.target.value as SellerConfig["activationMode"] })}>
            <option value="manual">Manual</option>
            <option value="automatic">Automatica</option>
          </select>
        </label>
        <label>Flujo default
          <select value={config.defaultActivationFlow} onChange={(event) => setConfig({ ...config, defaultActivationFlow: event.target.value as SellerConfig["defaultActivationFlow"] })}>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
          </select>
        </label>
        <label>Progreso
          <select value={config.progressionMode} onChange={(event) => setConfig({ ...config, progressionMode: event.target.value as SellerConfig["progressionMode"] })}>
            <option value="automatic">Automatico</option>
            <option value="manual_review">Revision manual</option>
          </select>
        </label>
        <label>Ordenes online seller
          <select value={String(config.onlineSellerOrdersEnabled)} onChange={(event) => setConfig({ ...config, onlineSellerOrdersEnabled: event.target.value === "true" })}>
            <option value="false">Inactivo</option>
            <option value="true">Activo</option>
          </select>
        </label>
        <label>Mini tiendas legacy
          <select value={String(config.legacySellerStoresEnabled)} onChange={(event) => setConfig({ ...config, legacySellerStoresEnabled: event.target.value === "true" })}>
            <option value="false">Inactivo</option>
            <option value="true">Activo</option>
          </select>
        </label>
        <label>Override lider
          <input type="number" step="0.01" value={config.leaderOverrideRate} onChange={(event) => setConfig({ ...config, leaderOverrideRate: Number(event.target.value) })} />
        </label>
      </div>
      <fieldset>
        <legend>Reglas de progreso</legend>
        <div className="level-rule-list">
          {config.levelRules.map((rule, index) => (
            <div className="level-rule-row" key={`${rule.id}-${index}`}>
              <label>ID
                <input value={rule.id} onChange={(event) => updateLevelRule(index, { id: event.target.value, level: event.target.value })} />
              </label>
              <label>Nombre
                <input value={rule.label} onChange={(event) => updateLevelRule(index, { label: event.target.value })} />
              </label>
              <label>Modo
                <select value={rule.sellerMode} onChange={(event) => updateLevelRule(index, { sellerMode: event.target.value as SellerMode, mode: event.target.value as SellerMode })}>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="leader">Leader</option>
                </select>
              </label>
              <label>Rank
                <input type="number" value={rule.rank} onChange={(event) => updateLevelRule(index, { rank: Number(event.target.value) })} />
              </label>
              <label>Comision
                <input type="number" step="0.01" value={rule.commissionRate} onChange={(event) => updateLevelRule(index, { commissionRate: Number(event.target.value) })} />
              </label>
              <label>Pedidos disbursed
                <input type="number" value={rule.conditions.minDisbursedOrders} onChange={(event) => updateLevelRuleConditions(index, { minDisbursedOrders: Number(event.target.value) })} />
              </label>
              <label>Ventas disbursed
                <input type="number" value={rule.conditions.minDisbursedSalesAmount} onChange={(event) => updateLevelRuleConditions(index, { minDisbursedSalesAmount: Number(event.target.value) })} />
              </label>
              <label>Moneda
                <input value={rule.conditions.currency} maxLength={3} onChange={(event) => updateLevelRuleConditions(index, { currency: event.target.value.toUpperCase() })} />
              </label>
              <label>Auto
                <select value={String(rule.autoAssignable)} onChange={(event) => updateLevelRule(index, { autoAssignable: event.target.value === "true" })}>
                  <option value="true">Si</option>
                  <option value="false">No</option>
                </select>
              </label>
              <label>Default modo
                <select value={String(rule.defaultForMode)} onChange={(event) => updateLevelRule(index, { defaultForMode: event.target.value === "true" })}>
                  <option value="false">No</option>
                  <option value="true">Si</option>
                </select>
              </label>
              <label>Activo
                <select value={String(rule.active)} onChange={(event) => updateLevelRule(index, { active: event.target.value === "true" })}>
                  <option value="true">Si</option>
                  <option value="false">No</option>
                </select>
              </label>
              <button className="ghost-button" type="button" onClick={() => removeLevelRule(index)}>
                <Trash2 size={16} /> Quitar
              </button>
            </div>
          ))}
        </div>
        <button className="secondary-button" type="button" onClick={addLevelRule}>
          <Plus size={16} /> Agregar nivel
        </button>
      </fieldset>
    </section>
  );
}

export function SellerCommercialEditor({
  seller,
  sellers,
  onSaved,
  onCancel
}: {
  seller: Seller;
  sellers: Seller[];
  onSaved: () => Promise<void> | void;
  onCancel: () => void;
}) {
  const auth = useAdminAuth();
  const [config, setConfig] = useState<SellerConfig>(defaultConfig);
  const [form, setForm] = useState({
    sellerMode: (seller.sellerMode || "starter") as SellerMode,
    commissionLevel: seller.commissionLevel || seller.level || "",
    commissionRate: String(seller.commissionRate ?? 0),
    overrideActive: Boolean(seller.commissionOverride?.active),
    overrideRate: seller.commissionOverride?.rate === undefined || seller.commissionOverride?.rate === null
      ? ""
      : String(seller.commissionOverride.rate),
    leaderSellerId: seller.leaderSellerId || "",
    reason: seller.commissionOverride?.reason || ""
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!auth.token) return;
      setConfig(await adminRequest<SellerConfig>(auth.token, "/v1/admin/seller-config"));
    }
    void load();
  }, [auth.token]);

  function applyLevel(levelId: string) {
    const rule = config.levelRules.find((item) => item.id === levelId || item.level === levelId);
    setForm((current) => ({
      ...current,
      commissionLevel: levelId,
      sellerMode: rule?.sellerMode || current.sellerMode,
      commissionRate: rule ? String(rule.commissionRate) : current.commissionRate
    }));
  }

  async function save() {
    if (!auth.token) return;
    setError(null);
    if (form.overrideActive && Number(form.overrideRate) <= 0) {
      setError("El override activo requiere una tasa mayor a cero.");
      return;
    }
    try {
      await adminRequest<Seller>(auth.token, `/v1/admin/sellers/${seller.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          sellerMode: form.sellerMode,
          commissionLevel: form.commissionLevel || undefined,
          commissionRate: Number(form.commissionRate),
          commissionOverride: {
            active: form.overrideActive,
            rate: form.overrideActive ? Number(form.overrideRate) : null,
            reason: form.reason || null
          },
          leaderSellerId: form.sellerMode === "leader" ? null : form.leaderSellerId || null,
          commercialUpdateReason: form.reason || "Ajuste manual admin"
        })
      });
      await onSaved();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No fue posible guardar el seller.");
    }
  }

  return (
    <section className="promotion-editor">
      <div className="promotion-editor-heading">
        <div>
          <span>Ajuste comercial</span>
          <h2>{seller.fullName}</h2>
        </div>
        <div className="button-row">
          <button className="ghost-button" type="button" onClick={onCancel}><X size={16} /> Cancelar</button>
          <button className="primary-button" type="button" onClick={() => void save()}><Save size={16} /> Guardar</button>
        </div>
      </div>
      <div className="form-grid">
        <label>Modo seller
          <select value={form.sellerMode} onChange={(event) => setForm({ ...form, sellerMode: event.target.value as SellerMode })}>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="leader">Leader</option>
          </select>
        </label>
        <label>Nivel comercial
          <select value={form.commissionLevel} onChange={(event) => applyLevel(event.target.value)}>
            <option value="">Sin nivel</option>
            {config.levelRules.map((rule) => (
              <option key={rule.id} value={rule.id}>{rule.label} ({percent(rule.commissionRate)})</option>
            ))}
          </select>
        </label>
        <label>Comision base
          <input type="number" step="0.01" value={form.commissionRate} onChange={(event) => setForm({ ...form, commissionRate: event.target.value })} />
        </label>
        <label>Override activo
          <select value={String(form.overrideActive)} onChange={(event) => setForm({ ...form, overrideActive: event.target.value === "true" })}>
            <option value="false">No</option>
            <option value="true">Si</option>
          </select>
        </label>
        <label>Comision override
          <input type="number" step="0.01" value={form.overrideRate} onChange={(event) => setForm({ ...form, overrideRate: event.target.value })} />
        </label>
        <label>Lider asignado
          <select
            value={form.leaderSellerId}
            disabled={form.sellerMode === "leader"}
            onChange={(event) => setForm({ ...form, leaderSellerId: event.target.value })}
          >
            <option value="">Sin lider</option>
            {sellers
              .filter((item) => item.id !== seller.id && item.status === "approved" && item.sellerMode === "leader")
              .map((item) => (
                <option key={item.id} value={item.id}>{item.fullName} ({item.code})</option>
              ))}
          </select>
        </label>
        <label>Motivo
          <textarea value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} />
        </label>
      </div>
      {error ? <p className="error-banner">{error}</p> : null}
    </section>
  );
}

function invitationUrl(invitation: SellerInvitation) {
  const baseUrl = String(import.meta.env.VITE_STOREFRONT_URL || "").replace(/\/$/, "");
  const path = `/seller?invite=${invitation.token}`;
  return baseUrl ? `${baseUrl}${path}` : path;
}

export function SellerInvitationsPanel({ sellers }: { sellers: Seller[] }) {
  const auth = useAdminAuth();
  const [items, setItems] = useState<SellerInvitation[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    type: "leader_activation" as SellerInvitation["type"],
    inviterSellerId: "",
    targetSellerMode: "",
    targetCommissionLevel: "leader_30",
    invitedEmail: "",
    maxUses: "1",
    expiresAt: "",
    notes: ""
  });

  const leaders = sellers.filter((seller) => seller.status === "approved" && seller.sellerMode === "leader");

  const load = useCallback(async () => {
    if (!auth.token) return;
    setItems(await adminRequest<SellerInvitation[]>(auth.token, "/v1/admin/seller-invitations"));
  }, [auth.token]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateType(type: SellerInvitation["type"]) {
    setForm((current) => ({
      ...current,
      type,
      inviterSellerId: type === "leader_activation" ? "" : current.inviterSellerId,
      targetSellerMode: type === "leader_activation" ? "leader" : "",
      targetCommissionLevel: type === "leader_activation" ? current.targetCommissionLevel || "leader_30" : "",
      maxUses: type === "leader_activation" ? "1" : ""
    }));
  }

  async function create() {
    if (!auth.token) return;
    setError(null);
    setMessage(null);
    if (form.type === "affiliate_activation" && !form.inviterSellerId) {
      setError("Selecciona el lider que invita.");
      return;
    }

    try {
      await adminRequest<SellerInvitation>(auth.token, "/v1/admin/seller-invitations", {
        method: "POST",
        body: JSON.stringify({
          type: form.type,
          inviterSellerId: form.type === "affiliate_activation" ? form.inviterSellerId : null,
          targetSellerMode: form.type === "leader_activation" ? "leader" : form.targetSellerMode || null,
          targetCommissionLevel: form.targetCommissionLevel || null,
          invitedEmail: form.invitedEmail || null,
          maxUses: form.maxUses ? Number(form.maxUses) : null,
          expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
          notes: form.notes || null
        })
      });
      setMessage("Invitacion creada.");
      setForm({
        type: "leader_activation",
        inviterSellerId: "",
        targetSellerMode: "leader",
        targetCommissionLevel: "leader_30",
        invitedEmail: "",
        maxUses: "1",
        expiresAt: "",
        notes: ""
      });
      await load();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "No fue posible crear la invitacion.");
    }
  }

  async function revoke(invitationId: string) {
    if (!auth.token) return;
    await adminRequest<SellerInvitation>(auth.token, `/v1/admin/seller-invitations/${invitationId}/revoke`, {
      method: "PATCH"
    });
    await load();
  }

  async function copy(invitation: SellerInvitation) {
    await navigator.clipboard.writeText(invitationUrl(invitation));
    setCopiedId(invitation.id);
  }

  return (
    <div className="promotion-admin-layout">
      <section className="promotion-editor">
        <div className="promotion-editor-heading">
          <div>
            <span>Invitaciones seller</span>
            <h2>Nueva invitacion</h2>
          </div>
          <button className="primary-button" type="button" onClick={() => void create()}>
            <UserRoundPlus size={16} /> Crear
          </button>
        </div>
        <div className="form-grid">
          <label>Tipo
            <select value={form.type} onChange={(event) => updateType(event.target.value as SellerInvitation["type"])}>
              <option value="leader_activation">Activar lider</option>
              <option value="affiliate_activation">Afiliado de lider</option>
            </select>
          </label>
          {form.type === "affiliate_activation" ? (
            <label>Lider que invita
              <select value={form.inviterSellerId} onChange={(event) => setForm({ ...form, inviterSellerId: event.target.value })}>
                <option value="">Selecciona lider</option>
                {leaders.map((seller) => <option key={seller.id} value={seller.id}>{seller.fullName} ({seller.code})</option>)}
              </select>
            </label>
          ) : null}
          <label>Modo objetivo
            <select
              value={form.type === "leader_activation" ? "leader" : form.targetSellerMode}
              disabled={form.type === "leader_activation"}
              onChange={(event) => setForm({ ...form, targetSellerMode: event.target.value })}
            >
              <option value="">El seller elige</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="leader">Leader</option>
            </select>
          </label>
          <label>Nivel objetivo
            <input value={form.targetCommissionLevel} onChange={(event) => setForm({ ...form, targetCommissionLevel: event.target.value })} />
          </label>
          <label>Email invitado
            <input type="email" value={form.invitedEmail} onChange={(event) => setForm({ ...form, invitedEmail: event.target.value })} />
          </label>
          <label>Usos maximos
            <input type="number" min="1" value={form.maxUses} onChange={(event) => setForm({ ...form, maxUses: event.target.value })} />
          </label>
          <label>Expira
            <input type="datetime-local" value={form.expiresAt} onChange={(event) => setForm({ ...form, expiresAt: event.target.value })} />
          </label>
          <label>Notas
            <textarea value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
          </label>
        </div>
        {message ? <p>{message}</p> : null}
        {error ? <p className="error-banner">{error}</p> : null}
      </section>
      <Table title="Invitaciones">
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <strong>{item.type === "leader_activation" ? "Lider" : "Afiliado"}</strong>
                <br />
                <small>{invitationUrl(item)}</small>
              </td>
              <td>
                {item.inviterSellerName || item.targetSellerMode || "-"}
                <br />
                <small>{item.maxUses ? `${item.usesCount}/${item.maxUses} usos` : `${item.usesCount} usos`}</small>
              </td>
              <td>
                <span className={`status status-${item.status}`}>{item.status}</span>
                <div className="table-actions">
                  <button type="button" onClick={() => void copy(item)}><Copy size={15} /> {copiedId === item.id ? "Copiado" : "Copiar"}</button>
                  <button type="button" disabled={item.status !== "active"} onClick={() => void revoke(item.id)}>Revocar</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export function ShareableContentPanel() {
  const auth = useAdminAuth();
  const [items, setItems] = useState<ShareableContent[]>([]);
  const [products, setProducts] = useState<AdminCatalogProduct[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [media, setMedia] = useState<Array<{
    id: string;
    asset?: ShareableContent["assets"][number];
    channel: ShareChannel;
    coverFile?: File | null;
    error?: string | null;
    file: File;
    status: "pending" | "uploading" | "ready" | "error";
  }>>([]);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    scope: "brand" as "brand" | "product",
    productId: "",
    tags: "",
    badges: "",
    templateText: "",
    channels: ["whatsapp_message"] as ShareChannel[],
    status: "active" as ShareableContent["status"]
  });
  const selectedProduct = products.find((product) => product.id === form.productId);
  const filteredProducts = products
    .filter((product) => {
      const term = productSearch.trim().toLowerCase();
      if (!term) return true;
      return [product.title, product.slug, product.id, product.insideProductId]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    })
    .slice(0, 8);

  const load = useCallback(async () => {
    if (!auth.token) return;
    setItems(await adminRequest<ShareableContent[]>(auth.token, "/v1/admin/shareable-content"));
    try {
      setProducts(await adminRequest<AdminCatalogProduct[]>(auth.token, "/v1/catalog/products"));
    } catch {
      setProducts([]);
    }
  }, [auth.token]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateChannels(channel: ShareChannel) {
    setForm((current) => ({
      ...current,
      channels: current.channels.includes(channel)
        ? current.channels.filter((item) => item !== channel)
        : [...current.channels, channel]
    }));
  }

  function addFiles(files: FileList | null) {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    const oversizedVideos = selected.filter((file) => (
      file.type.startsWith("video/") && file.size > SHAREABLE_VIDEO_MAX_BYTES
    ));
    const accepted = selected.filter((file) => !oversizedVideos.includes(file));

    if (oversizedVideos.length) {
      setError(`Cada video debe pesar maximo 70 MB. Reduce: ${oversizedVideos.map((file) => file.name).join(", ")}.`);
    } else {
      setError(null);
    }
    if (!accepted.length) return;

    setMedia((current) => [
      ...current,
      ...accepted.map((fileItem) => ({
        id: crypto.randomUUID(),
        channel: form.channels[0] || "generic",
        file: fileItem,
        status: "pending" as const
      }))
    ]);
  }

  function updateMedia(id: string, patch: Partial<(typeof media)[number]>) {
    setMedia((current) => current.map((item) => item.id === id ? { ...item, ...patch } : item));
  }

  function removeMedia(id: string) {
    setMedia((current) => current.filter((item) => item.id !== id));
  }

  function splitList(value: string) {
    return value
      .split(/[,#]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  async function uploadMediaAsset(item: (typeof media)[number], contentId: string) {
    if (!auth.token || !auth.user?.uid) throw new Error("Sesion admin no disponible.");
    const isVideo = item.file.type.startsWith("video/");
    const baseHeaders = {
      "x-content-id": contentId,
      "x-file-name": encodeURIComponent(item.file.name),
      "x-share-channel": item.channel
    };
    const asset = isVideo
      ? await uploadShareableVideo({
          adminUid: auth.user.uid,
          channel: item.channel,
          contentId,
          file: item.file
        })
      : await adminUploadRequest<ShareableContent["assets"][number]>(
          auth.token,
          "/v1/admin/shareable-content/assets",
          item.file,
          {
            ...baseHeaders,
            "x-asset-purpose": "media"
          }
        );

    if (!isVideo) return asset;
    if (!item.coverFile) throw new Error(`Agrega portada para ${item.file.name}.`);

    const cover = await adminUploadRequest<Partial<ShareableContent["assets"][number]>>(
      auth.token,
      "/v1/admin/shareable-content/assets",
      item.coverFile,
      {
        "x-content-id": contentId,
        "x-file-name": encodeURIComponent(item.coverFile.name),
        "x-share-channel": item.channel,
        "x-asset-purpose": "cover"
      }
    );

    return {
      ...asset,
      aspectRatio: asset.aspectRatio || cover.aspectRatio,
      coverStoragePath: cover.coverStoragePath || cover.previewStoragePath,
      coverUrl: cover.coverUrl || cover.previewUrl || cover.url,
      height: asset.height || cover.height,
      previewMimeType: cover.previewMimeType || cover.mimeType,
      previewSize: cover.previewSize || cover.size,
      previewStoragePath: cover.previewStoragePath,
      previewUrl: cover.previewUrl || cover.coverUrl || cover.url,
      url: cover.previewUrl || cover.coverUrl || cover.url || asset.url,
      width: asset.width || cover.width
    };
  }

  async function create() {
    if (!auth.token || creating) return;
    setError(null);
    setMessage(null);
    if (!form.title.trim()) {
      setError("Completa el titulo del contenido.");
      return;
    }
    if (form.scope === "product" && !form.productId) {
      setError("Selecciona el producto del catalogo.");
      return;
    }
    if (form.channels.length === 0) {
      setError("Selecciona al menos un destino ideal.");
      return;
    }
    if (media.length === 0) {
      setError("Agrega al menos una imagen o video.");
      return;
    }
    if (media.some((item) => item.file.type.startsWith("video/") && !item.coverFile)) {
      setError("Cada video necesita una portada de imagen optimizada.");
      return;
    }

    setCreating(true);
    try {
      const contentId = crypto.randomUUID();
      const uploadedAssets: ShareableContent["assets"] = [];

      for (const item of media) {
        updateMedia(item.id, { status: "uploading", error: null });
        try {
          const asset = await uploadMediaAsset(item, contentId);
          uploadedAssets.push(asset);
          updateMedia(item.id, { asset, status: "ready" });
        } catch (uploadError) {
          const uploadMessage = uploadError instanceof Error ? uploadError.message : "No fue posible subir este archivo.";
          updateMedia(item.id, { error: uploadMessage, status: "error" });
          throw uploadError;
        }
      }

      await adminRequest<ShareableContent>(auth.token, "/v1/admin/shareable-content", {
        method: "POST",
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          scope: form.scope,
          productId: form.scope === "product" ? form.productId : undefined,
          channels: form.channels,
          assets: uploadedAssets,
          tags: splitList(form.tags),
          badges: splitList(form.badges),
          templateText: form.templateText,
          status: form.status
        })
      });

      setMessage("Contenido creado y optimizado.");
      setForm({
        title: "",
        description: "",
        scope: "brand",
        productId: "",
        tags: "",
        badges: "",
        templateText: "",
        channels: ["whatsapp_message"],
        status: "active"
      });
      setProductSearch("");
      setMedia([]);
      await load();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "No fue posible crear el contenido.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="promotion-admin-layout">
      <section className="promotion-editor">
        <div className="promotion-editor-heading">
          <div>
            <span>Contenido seller</span>
            <h2>Nuevo contenido compartible</h2>
          </div>
          <button className="primary-button" type="button" disabled={creating} onClick={() => void create()}><UploadCloud size={16} /> {creating ? "Creando" : "Crear"}</button>
        </div>
        <div className="form-grid">
          <label>Titulo<input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
          <label>Destino del link
            <select value={form.scope} onChange={(event) => setForm({ ...form, scope: event.target.value as typeof form.scope })}>
              <option value="brand">Marca</option>
              <option value="product">Producto</option>
            </select>
          </label>
          {form.scope === "product" ? (
            <label>Producto del catalogo
              <span className="content-search-input"><Search size={15} /><input value={productSearch} placeholder="Buscar por nombre, slug o ID" onChange={(event) => setProductSearch(event.target.value)} /></span>
            </label>
          ) : null}
          <label>Estado
            <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as ShareableContent["status"] })}>
              <option value="active">Activo</option>
              <option value="draft">Borrador</option>
              <option value="archived">Archivado</option>
            </select>
          </label>
          <label>Descripcion<textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
          <label>Texto plantilla<textarea value={form.templateText} onChange={(event) => setForm({ ...form, templateText: event.target.value })} /></label>
          <label>Tags<input value={form.tags} placeholder="Oferta, nuevo producto, WhatsApp" onChange={(event) => setForm({ ...form, tags: event.target.value })} /></label>
          <label>Badges<input value={form.badges} placeholder="Alta conversion, Nuevo, Temporada" onChange={(event) => setForm({ ...form, badges: event.target.value })} /></label>
          <label>Media<input type="file" accept="image/*,video/*" multiple onChange={(event) => addFiles(event.target.files)} /></label>
        </div>
        {form.scope === "product" ? (
          <div className="content-product-picker">
            {selectedProduct ? (
              <div className="content-product-selected">
                <Check size={16} />
                <strong>{selectedProduct.title}</strong>
                <small>{selectedProduct.slug}</small>
              </div>
            ) : null}
            {filteredProducts.map((product) => (
              <button
                className={product.id === form.productId ? "active" : ""}
                key={product.id}
                type="button"
                onClick={() => {
                  setForm({ ...form, productId: product.id });
                  setProductSearch(product.title);
                }}
              >
                {product.images?.[0] ? <img src={product.images[0]} alt="" /> : <span>{product.title.slice(0, 2).toUpperCase()}</span>}
                <strong>{product.title}</strong>
                <small>{product.slug}</small>
              </button>
            ))}
          </div>
        ) : null}
        <fieldset>
          <legend>Destinos ideales</legend>
          <div className="placement-grid">
            {([
              ["whatsapp_message", "WhatsApp DM"],
              ["whatsapp_status", "Estado WhatsApp"],
              ["instagram_story", "IG Story"],
              ["instagram_feed", "IG Feed"],
              ["marketplace_listing", "Marketplace"],
              ["generic", "Generico"]
            ] as Array<[ShareChannel, string]>).map(([channel, label]) => (
              <label className="check-label" key={channel}>
                <input type="checkbox" checked={form.channels.includes(channel)} onChange={() => updateChannels(channel)} /> {label}
              </label>
            ))}
          </div>
        </fieldset>
        {media.length ? (
          <div className="content-media-list">
            {media.map((item) => (
              <article key={item.id} className="content-media-item" data-status={item.status}>
                <span className="content-media-icon">{item.file.type.startsWith("video/") ? <Film size={18} /> : <ImageIcon size={18} />}</span>
                <div>
                  <strong>{item.file.name}</strong>
                  <small>{Math.round(item.file.size / 1024)} KB · {item.file.type || "archivo"}</small>
                  {item.error ? <small className="content-media-error">{item.error}</small> : null}
                </div>
                <select value={item.channel} onChange={(event) => updateMedia(item.id, { channel: event.target.value as ShareChannel })}>
                  {form.channels.map((channel) => <option key={channel} value={channel}>{channel}</option>)}
                </select>
                {item.file.type.startsWith("video/") ? (
                  <label>Portada<input type="file" accept="image/*" onChange={(event) => updateMedia(item.id, { coverFile: event.target.files?.[0] ?? null })} /></label>
                ) : null}
                <button className="ghost-button" type="button" onClick={() => removeMedia(item.id)}><X size={15} /> Quitar</button>
              </article>
            ))}
          </div>
        ) : null}
        {message ? <p>{message}</p> : null}
        {error ? <p className="error-banner">{error}</p> : null}
      </section>
      <Table title="Contenido existente">
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <strong>{item.title}</strong><br />
                <small>{item.description}</small>
                {item.tags?.length ? <><br /><small>{item.tags.join(" · ")}</small></> : null}
              </td>
              <td>{item.scope}{item.productId ? <><br /><small>{item.productId}</small></> : null}</td>
              <td>{item.channels.join(", ")}<br /><small>{item.assets.length} media</small></td>
              <td><span className={`status status-${item.status}`}>{item.status}</span></td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export function SellerBonusesPanel() {
  const auth = useAdminAuth();
  const [items, setItems] = useState<SellerBonus[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [awards, setAwards] = useState<SellerBonusAward[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const emptyForm = {
    name: "",
    description: "",
    amount: "0",
    currency: "COP",
    rewardBucket: "commission" as "commission" | "storeCredit",
    conditionType: "manual" as SellerBonus["condition"]["type"],
    threshold: "",
    startsAt: "",
    endsAt: "",
    status: "active" as SellerBonus["status"],
    eligibleSellerModes: ["starter", "pro", "leader"] as SellerBonus["eligibleSellerModes"]
  };
  const [form, setForm] = useState(emptyForm);
  const [awardForm, setAwardForm] = useState({ bonusId: "", sellerId: "", notes: "" });

  const load = useCallback(async () => {
    if (!auth.token) return;
    try {
      const [bonuses, sellerList] = await Promise.all([
        adminRequest<SellerBonus[]>(auth.token, "/v1/admin/seller-bonuses"),
        adminRequest<Seller[]>(auth.token, "/v1/admin/sellers")
      ]);
      setItems(bonuses);
      setSellers(sellerList.filter((seller) => seller.status === "approved"));
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "No fue posible cargar bonos.");
    }
  }, [auth.token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadAwards(bonusId: string) {
    if (!auth.token || !bonusId) {
      setAwards([]);
      return;
    }
    try {
      setAwards(await adminRequest<SellerBonusAward[]>(auth.token, `/v1/admin/seller-bonuses/${bonusId}/awards`));
    } catch (loadError) {
      setAwards([]);
      setError(loadError instanceof Error ? loadError.message : "No fue posible cargar las acreditaciones.");
    }
  }

  function isoOrNull(value: string) {
    return value ? new Date(value).toISOString() : null;
  }

  function dateTimeInput(value?: string | null) {
    if (!value) return "";
    const date = new Date(value);
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }

  async function saveBonus() {
    if (!auth.token) return;
    setError(null);
    setMessage(null);
    const thresholdRequired = ["disbursed_order_count", "sales_amount"].includes(form.conditionType);
    if (!form.name.trim() || Number(form.amount) <= 0 || form.eligibleSellerModes.length === 0) {
      setError("Completa nombre, monto y al menos un modo elegible.");
      return;
    }
    if (thresholdRequired && Number(form.threshold) <= 0) {
      setError("La meta debe ser mayor que cero.");
      return;
    }
    const payload = {
      name: form.name,
      description: form.description || undefined,
      amount: { amount: Number(form.amount), currency: form.currency },
      rewardBucket: form.rewardBucket,
      condition: {
        type: form.conditionType,
        ...(thresholdRequired ? { threshold: Number(form.threshold) } : {}),
        ...(form.conditionType === "sales_amount" ? { currency: form.currency } : {})
      },
      startsAt: isoOrNull(form.startsAt),
      endsAt: isoOrNull(form.endsAt),
      eligibleSellerModes: form.eligibleSellerModes,
      maxAwardsPerSeller: 1,
      postAwardPolicy: "keep",
      status: form.status
    };

    try {
      await adminRequest<SellerBonus>(
        auth.token,
        editingId ? `/v1/admin/seller-bonuses/${editingId}` : "/v1/admin/seller-bonuses",
        {
          method: editingId ? "PUT" : "POST",
          body: JSON.stringify(payload)
        }
      );
      setMessage(editingId ? "Bono actualizado." : "Bono creado.");
      setEditingId(null);
      setForm(emptyForm);
      await load();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No fue posible guardar el bono.");
    }
  }

  function editBonus(item: SellerBonus) {
    setEditingId(item.id);
    setForm({
      name: item.name,
      description: item.description || "",
      amount: String(item.amount.amount),
      currency: item.amount.currency,
      rewardBucket: item.rewardBucket,
      conditionType: item.condition.type,
      threshold: item.condition.threshold ? String(item.condition.threshold) : "",
      startsAt: dateTimeInput(item.startsAt),
      endsAt: dateTimeInput(item.endsAt),
      status: item.status,
      eligibleSellerModes: item.eligibleSellerModes
    });
    setMessage(null);
  }

  function toggleMode(mode: "starter" | "pro" | "leader") {
    setForm((current) => ({
      ...current,
      eligibleSellerModes: current.eligibleSellerModes.includes(mode)
        ? current.eligibleSellerModes.filter((item) => item !== mode)
        : [...current.eligibleSellerModes, mode]
    }));
  }

  async function awardManualBonus() {
    if (!auth.token || !awardForm.bonusId || !awardForm.sellerId) return;
    setError(null);
    setMessage(null);
    if (awardForm.notes.trim().length < 3) {
      setError("Registra el motivo del bono manual.");
      return;
    }
    try {
      await adminRequest<SellerBonusAward>(auth.token, `/v1/admin/seller-bonuses/${awardForm.bonusId}/awards`, {
        method: "POST",
        body: JSON.stringify({ sellerId: awardForm.sellerId, notes: awardForm.notes })
      });
      setMessage("Bono acreditado al seller.");
      setAwardForm((current) => ({ ...current, sellerId: "", notes: "" }));
      await loadAwards(awardForm.bonusId);
    } catch (awardError) {
      setError(awardError instanceof Error ? awardError.message : "No fue posible acreditar el bono.");
    }
  }

  const manualBonuses = items.filter((item) => item.status === "active" && item.condition.type === "manual");
  const conditionLabel: Record<SellerBonus["condition"]["type"], string> = {
    manual: "Manual",
    first_disbursed_order: "Primera orden desembolsada",
    disbursed_order_count: "Cantidad de ordenes desembolsadas",
    sales_amount: "Ventas netas desembolsadas"
  };

  return (
    <div className="promotion-admin-layout">
      <section className="promotion-editor">
        <div className="promotion-editor-heading">
          <div>
            <span>Bonos seller</span>
            <h2>{editingId ? "Editar bono" : "Crear bono"}</h2>
          </div>
          <div className="button-row">
            {editingId ? (
              <button className="ghost-button" type="button" onClick={() => { setEditingId(null); setForm(emptyForm); }}>
                <X size={16} /> Cancelar
              </button>
            ) : null}
            <button className="primary-button" type="button" onClick={() => void saveBonus()}>
              <Save size={16} /> {editingId ? "Guardar" : "Crear"}
            </button>
          </div>
        </div>
        <div className="form-grid">
          <label>Nombre<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
          <label>Monto<input type="number" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} /></label>
          <label>Moneda<input value={form.currency} maxLength={3} onChange={(event) => setForm({ ...form, currency: event.target.value.toUpperCase() })} /></label>
          <label>Destino
            <select value={form.rewardBucket} onChange={(event) => setForm({ ...form, rewardBucket: event.target.value as typeof form.rewardBucket })}>
              <option value="commission">Comision disponible</option>
              <option value="storeCredit">Credito de tienda</option>
            </select>
          </label>
          <label>Condicion
            <select value={form.conditionType} onChange={(event) => setForm({ ...form, conditionType: event.target.value as typeof form.conditionType, threshold: "" })}>
              {Object.entries(conditionLabel).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          {["disbursed_order_count", "sales_amount"].includes(form.conditionType) ? (
            <label>Meta<input type="number" min="1" value={form.threshold} onChange={(event) => setForm({ ...form, threshold: event.target.value })} /></label>
          ) : null}
          <label>Inicio opcional<input type="datetime-local" value={form.startsAt} onChange={(event) => setForm({ ...form, startsAt: event.target.value })} /></label>
          <label>Fin opcional<input type="datetime-local" value={form.endsAt} onChange={(event) => setForm({ ...form, endsAt: event.target.value })} /></label>
          <label>Estado
            <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as typeof form.status })}>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
              <option value="archived">Archivado</option>
            </select>
          </label>
          <label>Descripcion<textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
        </div>
        <fieldset>
          <legend>Modos elegibles</legend>
          <div className="placement-grid">
            {(["starter", "pro", "leader"] as const).map((mode) => (
              <label className="check-label" key={mode}>
                <input type="checkbox" checked={form.eligibleSellerModes.includes(mode)} onChange={() => toggleMode(mode)} /> {mode}
              </label>
            ))}
          </div>
        </fieldset>
        {message ? <p>{message}</p> : null}
        {error ? <p className="error-banner">{error}</p> : null}
      </section>
      <Table title="Bonos">
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td><strong>{item.name}</strong><br /><small>{item.description}</small></td>
              <td>
                {conditionLabel[item.condition.type]}
                {item.condition.threshold ? `: ${item.condition.threshold}` : ""}
                <br /><small>{item.amount.currency} {item.amount.amount} a {item.rewardBucket}</small>
              </td>
              <td>
                <span className={`status status-${item.status}`}>{item.status}</span>
                <div className="table-actions"><button type="button" onClick={() => editBonus(item)}><Pencil size={15} /> Editar</button></div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <section className="promotion-editor">
        <div className="promotion-editor-heading">
          <div><span>Bono manual</span><h2>Acreditar a seller</h2></div>
          <button className="primary-button" type="button" onClick={() => void awardManualBonus()}><Gift size={16} /> Acreditar</button>
        </div>
        <div className="form-grid">
          <label>Bono
            <select
              value={awardForm.bonusId}
              onChange={(event) => {
                const bonusId = event.target.value;
                setAwardForm((current) => ({ ...current, bonusId }));
                void loadAwards(bonusId);
              }}
            >
              <option value="">Selecciona</option>
              {manualBonuses.map((bonus) => <option key={bonus.id} value={bonus.id}>{bonus.name}</option>)}
            </select>
          </label>
          <label>Seller
            <select value={awardForm.sellerId} onChange={(event) => setAwardForm((current) => ({ ...current, sellerId: event.target.value }))}>
              <option value="">Selecciona</option>
              {sellers.map((seller) => <option key={seller.id} value={seller.id}>{seller.fullName} ({seller.code})</option>)}
            </select>
          </label>
          <label>Motivo<textarea value={awardForm.notes} onChange={(event) => setAwardForm((current) => ({ ...current, notes: event.target.value }))} /></label>
        </div>
        {awardForm.bonusId ? <p>{awards.length} sellers acreditados con este bono.</p> : null}
      </section>
    </div>
  );
}

export function WalletAdjustmentPanel() {
  const auth = useAdminAuth();
  const [form, setForm] = useState({ uid: "", currency: "COP", bucket: "storeCredit", amount: "0", notes: "" });
  const [refundForm, setRefundForm] = useState({
    orderIdentifier: "",
    amount: "",
    commissionableAmount: "",
    currency: "COP",
    externalReference: "",
    reason: "",
    notes: ""
  });
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refundResult, setRefundResult] = useState<AdminOrderRefund | null>(null);
  const [refundContext, setRefundContext] = useState<AdminOrderRefundContext | null>(null);
  const [refundLookupLoading, setRefundLookupLoading] = useState(false);
  const [lastRefundLookup, setLastRefundLookup] = useState("");

  function amountInputValue(amount: number) {
    return amount > 0 ? String(amount) : "";
  }

  function updateRefundOrderIdentifier(value: string) {
    setRefundForm((current) => ({ ...current, orderIdentifier: value }));
    setRefundResult(null);
    if (value.trim() !== lastRefundLookup) {
      setRefundContext(null);
      setLastRefundLookup("");
    }
  }

  function updateRefundAmount(value: string) {
    setRefundForm((current) => {
      if (!refundContext) return { ...current, amount: value };
      const numericAmount = Math.max(Math.round(Number(value || 0)), 0);
      const suggestedCommissionable = value
        ? Math.min(numericAmount, refundContext.commissionableRefundableAmount)
        : 0;

      return {
        ...current,
        amount: value,
        commissionableAmount: amountInputValue(suggestedCommissionable)
      };
    });
  }

  async function loadRefundContext(identifier = refundForm.orderIdentifier) {
    if (!auth.token) return null;
    const cleanedIdentifier = identifier.trim();
    if (!cleanedIdentifier) return null;

    setError(null);
    setMessage(null);
    setRefundResult(null);
    setRefundLookupLoading(true);

    try {
      const context = await adminRequest<AdminOrderRefundContext>(
        auth.token,
        `/v1/admin/orders/${encodeURIComponent(cleanedIdentifier)}/refund-context`
      );
      setRefundContext(context);
      setLastRefundLookup(cleanedIdentifier);
      setRefundForm((current) => ({
        ...current,
        orderIdentifier: current.orderIdentifier || cleanedIdentifier,
        amount: amountInputValue(context.suggestedRefundAmount),
        commissionableAmount: amountInputValue(context.suggestedCommissionableAmount),
        currency: context.currency
      }));
      return context;
    } catch (lookupError) {
      setRefundContext(null);
      setLastRefundLookup("");
      setError(lookupError instanceof Error ? lookupError.message : "No fue posible buscar el pedido.");
      return null;
    } finally {
      setRefundLookupLoading(false);
    }
  }

  async function create() {
    if (!auth.token) return;
    setError(null);
    setMessage(null);
    try {
      await adminRequest(auth.token, "/v1/admin/wallet-adjustments", {
        method: "POST",
        body: JSON.stringify({ ...form, amount: Number(form.amount) })
      });
      setMessage("Ajuste aplicado.");
      setForm({ uid: "", currency: "COP", bucket: "storeCredit", amount: "0", notes: "" });
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "No fue posible aplicar el ajuste.");
    }
  }

  async function recordRefund() {
    if (!auth.token) return;
    setError(null);
    setMessage(null);
    setRefundResult(null);

    const orderIdentifier = refundForm.orderIdentifier.trim();
    const refundAmount = Number(refundForm.amount);
    const commissionableAmount = Number(refundForm.commissionableAmount || 0);

    if (!orderIdentifier || !refundForm.externalReference.trim() || !refundForm.reason.trim()) {
      setError("Completa pedido, referencia externa y motivo.");
      return;
    }

    if (!Number.isFinite(refundAmount) || refundAmount <= 0) {
      setError("El valor devuelto al cliente debe ser mayor que cero.");
      return;
    }

    if (!Number.isFinite(commissionableAmount) || commissionableAmount < 0) {
      setError("La base para reversar comision no puede ser negativa.");
      return;
    }

    try {
      const refund = await adminRequest<AdminOrderRefund>(
        auth.token,
        `/v1/admin/orders/${encodeURIComponent(orderIdentifier)}/refunds`,
        {
          method: "POST",
          body: JSON.stringify({
            amount: refundAmount,
            commissionableAmount,
            currency: refundForm.currency.toUpperCase(),
            externalReference: refundForm.externalReference,
            reason: refundForm.reason,
            notes: refundForm.notes || null
          })
        }
      );
      setRefundResult(refund);
      setMessage(refund.duplicate ? "Devolucion ya estaba registrada." : "Devolucion registrada.");
      setRefundForm({
        orderIdentifier: "",
        amount: "",
        commissionableAmount: "",
        currency: "COP",
        externalReference: "",
        reason: "",
        notes: ""
      });
      setRefundContext(null);
      setLastRefundLookup("");
    } catch (refundError) {
      setError(refundError instanceof Error ? refundError.message : "No fue posible registrar la devolucion.");
    }
  }

  return (
    <div className="promotion-admin-layout">
      <section className="promotion-editor">
        <div className="promotion-editor-heading">
          <div>
            <span>Wallet</span>
            <h2>Ajuste manual</h2>
          </div>
          <button className="primary-button" type="button" onClick={() => void create()}><Save size={16} /> Aplicar</button>
        </div>
        <div className="form-grid">
          <label>UID usuario<input value={form.uid} onChange={(event) => setForm({ ...form, uid: event.target.value })} /></label>
          <label>Bucket
            <select value={form.bucket} onChange={(event) => setForm({ ...form, bucket: event.target.value })}>
              <option value="storeCredit">Credito tienda</option>
              <option value="commission">Comision</option>
            </select>
          </label>
          <label>Monto<input type="number" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} /></label>
          <label>Notas<textarea value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} /></label>
        </div>
      </section>

      <section className="promotion-editor">
        <div className="promotion-editor-heading">
          <div>
            <span>Devoluciones</span>
            <h2>Registrar devolucion</h2>
          </div>
          <div className="button-row">
            <button className="secondary-button" type="button" disabled={refundLookupLoading || !refundForm.orderIdentifier.trim()} onClick={() => void loadRefundContext()}>
              <Search size={16} /> {refundLookupLoading ? "Buscando" : "Buscar"}
            </button>
            <button className="primary-button" type="button" onClick={() => void recordRefund()}><RotateCcw size={16} /> Registrar</button>
          </div>
        </div>
        <div className="form-grid">
          <label>Pedido<input placeholder="orderId, #OV-123 o 123" value={refundForm.orderIdentifier} onBlur={() => {
            const identifier = refundForm.orderIdentifier.trim();
            if (identifier && identifier !== lastRefundLookup) void loadRefundContext(identifier);
          }} onChange={(event) => updateRefundOrderIdentifier(event.target.value)} /></label>
          <label>Valor devuelto al cliente
            <input type="number" min="0" value={refundForm.amount} onChange={(event) => updateRefundAmount(event.target.value)} />
            {refundContext ? <small>Max {adminMoney(refundContext.customerRefundableAmount, refundContext.currency)}</small> : null}
          </label>
          <label>Base para reversar comision
            <input type="number" min="0" value={refundForm.commissionableAmount} onChange={(event) => setRefundForm({ ...refundForm, commissionableAmount: event.target.value })} />
            {refundContext ? <small>Max {adminMoney(refundContext.commissionableRefundableAmount, refundContext.currency)}</small> : null}
          </label>
          <label>Moneda<input value={refundForm.currency} maxLength={3} disabled={Boolean(refundContext)} onChange={(event) => setRefundForm({ ...refundForm, currency: event.target.value.toUpperCase() })} /></label>
          <label>Referencia externa<input value={refundForm.externalReference} onChange={(event) => setRefundForm({ ...refundForm, externalReference: event.target.value })} /></label>
          <label>Motivo<textarea value={refundForm.reason} onChange={(event) => setRefundForm({ ...refundForm, reason: event.target.value })} /></label>
          <label>Notas<textarea value={refundForm.notes} onChange={(event) => setRefundForm({ ...refundForm, notes: event.target.value })} /></label>
        </div>
        {refundContext ? (
          <fieldset>
            <legend>Pedido encontrado</legend>
            <div className="refund-context-grid">
              <div><span>Order ID</span><strong>{refundContext.orderId}</strong></div>
              <div><span>Inside</span><strong>{refundContext.insideOrderNumber || "Sin numero"}</strong></div>
              <div><span>Estado</span><strong>{refundContext.orderLifecycleStatus || refundContext.status || "Sin estado"}</strong></div>
              <div><span>Seller</span><strong>{refundContext.sellerCode || refundContext.sellerId || "Sin seller"}</strong></div>
              <div><span>Total orden</span><strong>{adminMoney(refundContext.orderTotalAmount, refundContext.currency)}</strong></div>
              <div><span>Ya devuelto</span><strong>{adminMoney(refundContext.customerRefundedAmount, refundContext.currency)}</strong></div>
              <div><span>Base comisionable</span><strong>{adminMoney(refundContext.commissionBaseAmount, refundContext.currency)}</strong></div>
              <div><span>Base ya devuelta</span><strong>{adminMoney(refundContext.commissionableRefundedAmount, refundContext.currency)}</strong></div>
            </div>
          </fieldset>
        ) : null}
        {refundResult ? (
          <p>
            Reversa seller {adminMoney(refundResult.sellerCommissionReversedAmount, refundResult.amount.currency)}
            {" - "}
            lider {adminMoney(refundResult.leaderOverrideReversedAmount, refundResult.amount.currency)}
            {" - "}
            {refundResult.refundStatus}
          </p>
        ) : null}
      </section>

      {message ? <p>{message}</p> : null}
      {error ? <p className="error-banner">{error}</p> : null}
    </div>
  );
}

function Table({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="table-card">
      <div className="table-header"><h2>{title}</h2></div>
      <div className="table-scroll">
        <table>
          <thead><tr><th>Nombre</th><th>Tipo/Monto</th><th>Estado</th></tr></thead>
          {children}
        </table>
      </div>
    </section>
  );
}
